// Web replacement for react-native-camera-kit's QR scanner: getUserMedia + BarcodeDetector,
// with jsQR as the fallback for browsers without BarcodeDetector (iOS Safari).
import React, { useEffect, useRef } from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';
import jsQR from 'jsqr';

type ReadCodeEvent = { nativeEvent: { codeStringValue: string; codeFormat?: string } };

type Props = {
  style?: StyleProp<ViewStyle>;
  scanBarcode?: boolean;
  onReadCode?: (event: ReadCodeEvent) => void;
  onError?: (error: unknown) => void;
  frameColor?: string;
  [key: string]: unknown;
};

type Detector = { detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string; format: string }>> };

export function Camera({ style, scanBarcode = true, onReadCode, onError, frameColor = '#fff' }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onReadRef = useRef(onReadCode);
  onReadRef.current = onReadCode;

  useEffect(() => {
    let stream: MediaStream | undefined;
    let frame = 0;
    let stopped = false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const BarcodeDetector = (window as unknown as { BarcodeDetector?: new (o: object) => Detector }).BarcodeDetector;
    const detector = BarcodeDetector ? new BarcodeDetector({ formats: ['qr_code'] }) : null;

    const scan = async () => {
      const video = videoRef.current;
      if (stopped || !video) return;
      if (scanBarcode && video.readyState >= video.HAVE_ENOUGH_DATA) {
        let value: string | undefined;
        if (detector) {
          value = (await detector.detect(video).catch(() => []))[0]?.rawValue;
        } else if (ctx) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          value = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height)?.data;
        }
        if (value) onReadRef.current?.({ nativeEvent: { codeStringValue: value, codeFormat: 'qr' } });
      }
      frame = requestAnimationFrame(scan);
    };

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(s => {
        stream = s;
        if (stopped || !videoRef.current) return;
        videoRef.current.srcObject = s;
        return videoRef.current.play().then(scan);
      })
      .catch(error => onError?.(error));

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanBarcode]);

  return (
    <View style={[{ overflow: 'hidden', backgroundColor: '#000' }, style]}>
      <video
        ref={videoRef}
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 240,
          height: 240,
          marginLeft: -120,
          marginTop: -120,
          borderWidth: 2,
          borderColor: frameColor,
          borderRadius: 16,
        }}
      />
    </View>
  );
}

export default Camera;
